import {Component, OnInit} from '@angular/core';
import {Alarm} from '../../../model/alarm';
import {ActivatedRoute} from '@angular/router';
import {AuthGuard} from '../../../common/auth-guard.service';
import {AlarmsService} from '../../../services/alarms.service';
import {SortOrder} from '../../../model/sort-order';
import {MachineGroupsService} from 'src/app/services/machine-groups.service';
import {ColorService} from 'src/app/services/color.service';
import {HierarchyService} from 'src/app/services/hierarchy.service';
import {AlarmFilters} from 'src/app/model/filters/alarm-filters';
import {TranslocoService} from '@ngneat/transloco';
import {CommonMessagesService} from 'src/app/services/common-messages.service';
import {MachineGroup} from "../../../model/machine-group";
import {TimezoneService} from "../../../services/timezone.service";
import {CommonTranslationsService} from "../../../services/common-translations.service";
import {AppConfig} from "../../../shared/global-variables";

@Component({
  selector: 'app-alarm-overview',
  templateUrl: './alarm-overview.component.html',
  styleUrls: ['./alarm-overview.component.scss']
})
export class AlarmOverviewComponent implements OnInit {

  public alarms: Alarm[];
  public totalAlarms: number;
  public areAlarmsLoading: boolean;
  public areAlarmChartsLoaded: boolean = false;

  public rows: number = 10;
  public first: number = 0;

  public filtersVisible: boolean;
  public currentFilters: AlarmFilters;

  public activationDate: number;
  public deactivationDate: number;

  public machineGroups: MachineGroup[];
  public hierarchy: any[];
  public organizations: any[];

  public alarmStatus: [{ data: { name: string, y: number, color: string }[] }] = [{data: []}]
  public alarmStatusCategories: string[];
  public alarmDistribution: [{ data: { name: string, y: number, color: string }[] }] = [{data: []}];
  public alarmDistributionCategories: string[] = [];

  public operationsTranslation: { [key: string]: string } = {};

  public pointFormatPieChart: string;

  public availableYearRange: string;

  private readonly colors: string[] = [];
  private activeTranslation: string;
  private inactiveTranslation: string;


  constructor(private route: ActivatedRoute,
              private alarmsService: AlarmsService,
              private machineGroupsService: MachineGroupsService,
              private colorService: ColorService,
              private hierarchyService: HierarchyService,
              private commonTranslationsService: CommonTranslationsService,
              private readonly i18n: TranslocoService,
              private readonly commonMessagesService: CommonMessagesService,
              private readonly timezoneService: TimezoneService,
              public authGuard: AuthGuard
  ) {
    this.currentFilters = new AlarmFilters();
    this.colors = AppConfig.COLORS;
  }

  ngOnInit(): void {
    this.alarms = new Array(this.rows);
    this.availableYearRange = this.timezoneService.calcYearRange();

    this.i18n.selectTranslation().subscribe(() => {
      this.activeTranslation = this.i18n.translate('common.active');
      this.inactiveTranslation = this.i18n.translate('common.inactive');
      this.pointFormatPieChart = this.i18n.translate('misc.alarms') + ': {point.y} ({point.percentage:.1f} %)<br>' + this.i18n.translate('attribute.total') + ': {point.total}';
    });
    this.commonTranslationsService.getOperationsTranslation().subscribe(operationsTranslation => {
      this.operationsTranslation = operationsTranslation;
    });
    this.loadFilters();
  }

  private loadAlarms(offset: number, limit: number): void {
    this.areAlarmsLoading = true;
    this.alarms = new Array(this.rows);
    this.trimFieldsForm(this.currentFilters);
    this.alarmsService.getAlarms(offset, limit, this.currentFilters).subscribe((response) => {
      this.alarms = response.data.content;
      this.totalAlarms = response.data.totalElements;

      if (this.alarms.length > 0 && !this.areAlarmChartsLoaded) {
        this.alarmsService.getAlarmsCharts().subscribe((response) => {
          this.alarmStatus = [{
            data: [
              {
                name: this.activeTranslation,
                y: response.activeAlarmsNumber,
                color: getComputedStyle(document.documentElement).getPropertyValue('--primary-color')
              },
              {
                name: this.inactiveTranslation,
                y: response.inactiveAlarmsNumber,
                color: getComputedStyle(document.documentElement).getPropertyValue('--custom-gray')
              }
            ]
          }];
          this.alarmStatusCategories = [this.activeTranslation, this.inactiveTranslation];
          this.alarmDistribution = [{
            data: Object.entries(response.alarmDistribution).map((entry, index) => {
              return {name: entry[0], y: entry[1], color: this.colors[index % this.colors.length]}
            })
          }]
          this.alarmDistributionCategories = Object.keys(response.alarmDistribution);
        }).add(() => this.areAlarmChartsLoaded = true)
      } else this.areAlarmChartsLoaded = true;
    }).add(() => this.areAlarmsLoading = false)
  }

  deactivateAlarm(alarmUuid: string): void {
    this.commonMessagesService.showRequestConfirmation('info.deactivateAlarm', () => {
      this.alarmsService.deactivateAlarm(alarmUuid).subscribe(() => {
        this.commonMessagesService.showSuccessfullyResetMessage();
        this.loadAlarms(this.first / this.rows, this.rows);
      }, () => {
        this.commonMessagesService.showErrorWhileResettingAlarmMessage();
      });
    });
  }

  trimFieldsForm(filters: AlarmFilters): void {
    Object.keys(filters).forEach((key) => {
      if (filters[key] != undefined && typeof filters[key] === 'string') {
        filters[key] = filters[key].trim();
      }
    });
  }

  onPageChange(event): void {
    this.currentFilters.sortField = event.sortField;
    this.currentFilters.sortOrder = event.sortOrder < 0 ? SortOrder.DESCENDING : SortOrder.ASCENDING;
    this.route.params.subscribe(params => this.loadAlarms(event.first / event.rows, event.rows));
  }

  loadFilters(): void {
    if (this.authGuard.hasPermission('PERMISSION_MANAGE_ADVANCED_MACHINES')) {
      this.machineGroupsService.getGroupsSummary().subscribe(response => {
        this.machineGroups = response.machineGroups;
      });
    }
    this.buildTreeNode();
  }

  private buildTreeNode(): void {
    this.hierarchyService.getHierarchyDropdown().subscribe(response => {
      this.hierarchy = this.hierarchyService.getHierarchyTreeNodeFilter(response.hierarchyDto)[1];
    });
  }

  resetFilters(): void {
    this.currentFilters = new AlarmFilters();
    this.activationDate = null;
    this.deactivationDate = null;
    this.currentFilters.sortField = 'deactivationDate';
    this.currentFilters.sortOrder = 1;
    this.organizations = [];
  }

  applyFilters(): void {
    this.first = 0;
    if (this.organizations != null && this.organizations.length > 0) {
      this.currentFilters.organizationUuids = this.organizations.map(organization => organization.data);
    } else {
      delete this.currentFilters['organizationUuids'];
    }

    this.loadAlarms(this.first / this.rows, this.rows);
  }

  setDate(name: string, value: Date): void {
    if (value) this.currentFilters[name] = this.timezoneService.timestampFromCalendarDataWithTime(value);
    else delete this.currentFilters[name];
  }

  adjustTextColor(colorHex): string {
    return this.colorService.adjustTextColor(colorHex);
  }

}
